import { Schema } from "effect";
import {
  ConversationId,
  ConversationThreadId,
  DraftMessageId,
  MessageId,
} from "../../../ids/definitions";
import { MongoUserId } from "../../../ids/mongo-ids";
import { EmailRecipientSchema } from "./conversation";
import { MessageChannel, MessageSenderType } from "./message";
import { Attachment } from "./message/attachment";

// ===== Channel types =====

const EmailChannel = Schema.Literal("gmail", "outlook");

const NonEmailChannel = Schema.Literal(
  "facebookDm",
  "facebookComments",
  "instagramDm",
  "instagramComments",
  "redoChat",
  "sms",
  "attentive",
  "postscript",
  "tiktok",
  "voice",
  "contactForm",
  "internalNote",
  "mobileMessage",
);

// ===== Channel data =====

export const EmailDraftChannelData = Schema.Struct({
  to: Schema.optional(Schema.Array(EmailRecipientSchema)),
  cc: Schema.optional(Schema.Array(EmailRecipientSchema)),
  bcc: Schema.optional(Schema.Array(EmailRecipientSchema)),
});
export type EmailDraftChannelData = typeof EmailDraftChannelData.Type;

// ===== SaveDraftInput =====

const SaveDraftInputBase = {
  threadId: ConversationThreadId,
  inReplyToMessageId: Schema.optional(MessageId),
  content: Schema.String,
  attachments: Schema.Array(Attachment),
};

const EmailSaveDraftInput = Schema.Struct({
  ...SaveDraftInputBase,
  channel: EmailChannel,
  channelData: EmailDraftChannelData,
});

const NonEmailSaveDraftInput = Schema.Struct({
  ...SaveDraftInputBase,
  channel: NonEmailChannel,
  channelData: Schema.Null,
});

export const SaveDraftInput = Schema.Union(
  EmailSaveDraftInput,
  NonEmailSaveDraftInput,
);
export type SaveDraftInput = typeof SaveDraftInput.Type;

// ===== DraftResponse =====

export const DraftMessageStatus = Schema.Literal("PENDING", "SENDING", "ERROR");
export type DraftMessageStatus = typeof DraftMessageStatus.Type;

const DraftResponseBase = {
  id: DraftMessageId,
  conversationId: ConversationId,
  threadId: ConversationThreadId,
  inReplyToMessageId: Schema.OptionFromUndefinedOr(MessageId),
  senderMongoUserId: Schema.NullOr(MongoUserId),
  senderType: MessageSenderType,
  content: Schema.String,
  attachments: Schema.Array(Attachment),
  status: DraftMessageStatus,
  createdAt: Schema.DateTimeUtc,
  updatedAt: Schema.DateTimeUtc,
};

const EmailDraftResponse = Schema.Struct({
  ...DraftResponseBase,
  channel: EmailChannel,
  channelData: EmailDraftChannelData,
});

const NonEmailDraftResponse = Schema.Struct({
  ...DraftResponseBase,
  channel: NonEmailChannel,
  channelData: Schema.Null,
});

export const DraftResponse = Schema.Union(
  EmailDraftResponse,
  NonEmailDraftResponse,
);
export type DraftResponse = typeof DraftResponse.Type;

export const ThreadDraftsResponse = Schema.Struct({
  drafts: Schema.Array(DraftResponse),
});
export type ThreadDraftsResponse = typeof ThreadDraftsResponse.Type;

// ===== SendMessagePayload (RPC) =====

/**
 * RPC payload for sending a message. All sends require a draft.
 * `actor` and `conversationId` are set server-side from auth context and URL path.
 */
export const SendMessagePayload = Schema.Struct({
  channel: MessageChannel,
  threadId: ConversationThreadId,
  draftId: DraftMessageId,
});
export type SendMessagePayload = typeof SendMessagePayload.Type;

export class NotImplementedError extends Schema.TaggedError<NotImplementedError>()(
  "NotImplementedError",
  { message: Schema.String },
) {}
